import React, { PropsWithChildren } from 'react';
import {
  ModuleTopBar,
  Box,
  Flex,
  Grid,
  Text,
  Select,
  VStack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import AnvaFlowProductsBottomBar from '@flows/src/components/flow-products/AnvaFlowProductsBottomBar';
import AnvaFlowProductsSidebar from '@flows/src/components/flow-products/AnvaFlowProductsSidebar';
import { AnvaBasketCalculationProvider } from '@flows/src/components/flow-products/quotes/basket/AnvaBasketCalculationProvider';
import YellowHiveBasketCalculationProvider from '@flows/src/components/flow-products/quotes/basket/YellowHiveBasketCalculationProvider';
import { useHistory, useParams } from 'react-router';
import useQueryParams from '@common/src/hooks/useQueryParams';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import useStartAnvaFlow from '@sales-conversation/src/hooks/anva/useStartAnvaFlow';
import useStartYellowhiveFlow from '@flows/src/hooks/flows-configure/useStartYellowhiveFlow';
import { useQueryClient } from 'react-query';
import YellowhiveFlowProductsSidebar from '@flows/src/components/flow-products/YellowhiveFlowProductsSidebar';
import YellowhiveFlowProductsBottomBar from '@flows/src/components/flow-products/YellowhiveFlowProductsBottomBar';
import { useFlowInfo } from '@flows/src/api/getFlowInfo';
import { useYellowhiveQuotes } from '@flows/src/api/yellowhive-flows';
import { SchemaYellowHiveQuoteCalculationJob } from 'wg-api-sdk';
import { useFlowProductsOverviewParams } from '@flows/src/hooks/flow-products/useFlowProductsOverviewParams';
import { FLOWS_PRODUCTS_PATH } from '@flows/src/FlowsProductsRouter';
import useYellowhiveFlowProductSelectionStore from '@flows/src/store/zustand/flows-products/useYellowhiveFlowProductSelectionStore';

const FlowProductsLayout: React.FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const sessionId = queryParams.get('session_id') || undefined;
  const { data: session } = useFlowsSession(sessionId);
  const startFlow = useStartAnvaFlow();
  const history = useHistory();
  const { flowId } = useParams<{ flowId: string }>();
  const queryClient = useQueryClient();
  const { data: flowInfo } = useFlowInfo(flowId || '');
  const startYellowhiveFlow = useStartYellowhiveFlow();
  const { tagId } = useFlowProductsOverviewParams();
  const resetYellowhiveSelectionStore = useYellowhiveFlowProductSelectionStore(
    (state) => state.resetYellowhive,
  );
  const { data: yellowhiveQuotesData } = useYellowhiveQuotes({
    sessionId,
    queryOptions: {
      enabled: !!sessionId && flowInfo?.sub_type === 'YELLOWHIVE',
    },
  });

  // Build categories for mobile selector
  const categories =
    yellowhiveQuotesData?.quotes?.reduce(
      (
        uniqueItems: { key: string; label: string }[],
        { insurance }: SchemaYellowHiveQuoteCalculationJob,
      ) => {
        if (!uniqueItems.some((item) => item.key === insurance.code)) {
          uniqueItems.push({
            key: insurance.code,
            label: insurance.name,
          });
        }
        return uniqueItems;
      },
      [] as { key: string; label: string }[],
    ) || [];

  // Handle category change for mobile
  const handleCategoryChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newCategoryId = event.target.value;
    if (newCategoryId && flowId) {
      history.push({
        pathname: `${FLOWS_PRODUCTS_PATH}/${flowId}/overview/${newCategoryId}`,
        search: window.location.search,
      });
    }
  };

  const getBackText = () => {
    if (window.location.pathname.includes('selection-overview')) {
      return t('BACK-TO-DASHBOARD');
    }
    if (window.location.pathname.includes('basket')) {
      return t('BACK-TO-OVERVIEW');
    }
    return t('Back to questions');
  };
  const handlePrevious = () => {
    if (window.location.pathname.includes('basket')) {
      if (flowInfo?.sub_type === 'YELLOWHIVE') {
        // clear selected yellowhive products
        resetYellowhiveSelectionStore();
      }
      // Go back to the previous page (most likely the overview page with the
      // correct tagId preserved in the URL) instead of pushing a new URL that
      // might lose the tagId. This prevents navigation to `/overview` without
      // a tag and ensures the user returns to the exact overview they came
      // from.
      return history.goBack();
    }
    if (flowId && sessionId) {
      // Clear the flow-products cache when navigating back to questionnaire
      // This ensures that when the user returns to the flow-products overview,
      // new quotes are calculated and the redirect logic works properly
      queryClient.removeQueries(['flow-products', 'anva', 'quotes']);
      queryClient.removeQueries(['flow-products', 'anva', 'completed-quotes']);
      queryClient.removeQueries(['flow-products', 'anva', 'quotes-v2']);
      // Also clear YellowHive caches if applicable
      queryClient.removeQueries(['flow-products', 'yellowhive', 'quotes']);

      // history.push(`/flows/${flowId}/questions/${sessionId}`);
      if (flowInfo?.sub_type === 'YELLOWHIVE') {
        startYellowhiveFlow({
          flowId: flowId,
          uuid: sessionId,
          partyId: session?.prospect?.id,
          yellowhive_products: session?.yellowhive_products || [],
        });
      } else {
        startFlow({
          flowId: flowId,
          uuid: sessionId,
          partyId: session?.prospect?.id,
          anva_products: session?.anva_products || [],
        });
      }
    }
  };

  const getTopBarText = (): string => {
    if (window.location.pathname.includes('selection-overview')) {
      return t('FLOWS_PRODUCTS_SELECTION-OVERVIEW_TOP-BAR_TITLE');
    }
    if (flowInfo?.sub_type === 'YELLOWHIVE') {
      return t('FLOWS_PRODUCTS_SELECTION-OVERVIEW_TOP-BAR_TITLE-YELLOWHIVE');
    }
    return t('CONVERSATIONS_REPORT-CREATION_PRODUCT-SELECTION');
  };

  const checkIsBasketPage = () => {
    const path = window.location.pathname;
    return path.includes('basket') || path.includes('selection-overview');
  };
  const isBasketPage = checkIsBasketPage();

  // The core layout content
  const layoutContent = (
    <Grid
      templateColumns={{
        base: '1fr', // Mobile: single column
        tablet: 'auto minmax(0, 1fr)', // Tablet+: sidebar + main content
      }}
      w="100%"
      h="100%"
      position="relative"
      minH="0"
    >
      {/* Sidebar - hidden on mobile */}
      <Box display={{ base: 'none', tablet: 'block' }}>
        {flowInfo?.sub_type === 'ANVA' && <AnvaFlowProductsSidebar />}
        {flowInfo?.sub_type === 'YELLOWHIVE' && (
          <YellowhiveFlowProductsSidebar />
        )}
      </Box>

      <Grid templateRows="auto 1fr auto" position="relative" w="100%" minW="0">
        {/* Mobile Header */}
        <Box
          display={{ base: 'block', tablet: 'none' }}
          position="sticky"
          top="0"
          bg="white"
          zIndex="2"
          borderBottom="1px"
          borderColor="gray.50"
        >
          <VStack spacing="0" align="stretch">
            {/* Title */}
            <Flex align="center" justify="center" px="4" py="3" h="60px">
              <Text
                textStyle="h6Headline"
                textAlign="center"
                fontSize="md"
                fontWeight="600"
                color="gray.800"
                noOfLines={1}
              >
                {getTopBarText()}
              </Text>
            </Flex>

            {/* Category Selector for Yellowhive - only show on overview pages */}
            {flowInfo?.sub_type === 'YELLOWHIVE' &&
              categories.length > 1 &&
              !window.location.pathname.includes('basket') &&
              !window.location.pathname.includes('selection-overview') && (
                <Box
                  px="4"
                  pb="3"
                  pt="1"
                  borderTop="1px"
                  borderColor="gray.100"
                >
                  <Text fontSize="sm" color="gray.500" mb="2" fontWeight="500">
                    {t('Category')}:
                  </Text>
                  <Select
                    value={tagId || ''}
                    onChange={handleCategoryChange}
                    bg="white"
                    borderColor="gray.200"
                    borderRadius="lg"
                    h="44px"
                    fontSize="md"
                    fontWeight="500"
                    _focus={{
                      borderColor: 'primary.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)',
                    }}
                    _hover={{
                      borderColor: 'gray.300',
                    }}
                  >
                    {categories.map((category) => (
                      <option key={category.key} value={category.key}>
                        {category.label}
                      </option>
                    ))}
                  </Select>
                </Box>
              )}
          </VStack>
        </Box>

        {/* Desktop Header */}
        <Box
          display={{ base: 'none', tablet: 'block' }}
          position="sticky"
          top="0"
          bg="white"
          zIndex="1"
        >
          <ModuleTopBar title={getTopBarText()} />
        </Box>

        <Flex
          flexDirection="column"
          align="center"
          flexGrow="1"
          bg="gray.ultraLight"
          overflow="auto"
          minW="0"
        >
          {children}
        </Flex>
        {flowInfo?.sub_type === 'ANVA' && (
          <AnvaFlowProductsBottomBar
            handlePrevious={handlePrevious}
            backText={getBackText()}
          />
        )}
        {flowInfo?.sub_type === 'YELLOWHIVE' && (
          <YellowhiveFlowProductsBottomBar
            handlePrevious={handlePrevious}
            backText={getBackText()}
          />
        )}
      </Grid>
    </Grid>
  );

  // Wrap with appropriate BasketCalculationProvider when on basket page so that calculation
  // state is shared between the page content and the bottom bar actions.
  if (isBasketPage) {
    if (flowInfo?.sub_type === 'YELLOWHIVE') {
      return (
        <YellowHiveBasketCalculationProvider sessionId={sessionId || ''}>
          {layoutContent}
        </YellowHiveBasketCalculationProvider>
      );
    }
    return (
      <AnvaBasketCalculationProvider sessionId={sessionId || ''}>
        {layoutContent}
      </AnvaBasketCalculationProvider>
    );
  }

  return layoutContent;
};

export default FlowProductsLayout;
